import React from "react";
import Breadcrumb from "components/Breadcrumb";
import Checklist from "components/list/Checklist";
import FinancingData from "components/FinancingData";
import FinancingDetailsOverlay from "components/FinancingDetailsOverlay";
import Quickfinder from "components/quickfinder/Quickfinder";
import withObjectFitSupportDetection from "hocs/withObjectFitSupportDetection";
import buildHeroImageUrls from "utils/buildHeroImageUrls";

import BrandModelHeroImage from "./BrandModelHeroImage";
import OutOfStockNotice from "./OutOfStockNotice";

const BrandModelHero = ({
  brand,
  brandPageUrl,
  model = null,
  breadcrumbItems,
  heroImageBase,
  heroImagePosition,
  heroImageWithBackground,
  heroImageFullCover,
  heroTextInverted,
  minLeasingRate,
  hasOffers,
  objectFitSupported,
  showModelTypeSelect = false,
  modelTypes = [],
  cheapestProduct,
  quickfinderFilters,
}) => (objectFitSupported ? (
  <div
    className={`brand-model-hero ${
        heroImageWithBackground ? "brand-model-hero--with-background" : ""
      } relative h-full xl:mb-15`}
    data-brand-name={brand}
  >
    <div
      className={`z-10 ${
          heroImageWithBackground ? "relative" : "xl:absolute xl:pin-x"
        }`}
    >
      <Breadcrumb
        items={breadcrumbItems}
        color={heroTextInverted && "white"}
      />
      <div
        className={`pt-6 lg:pt-15 px-5 lg:px-10 ${
            heroImageWithBackground ? "mb-6 xl:mb-15" : ""
          }`}
      >
        <h1
          className={`text-center mb-3 lg:mb-5 ${
              heroTextInverted ? "xl:text-white xl:text-shadow" : ""
            }`}
        >
          {brand}
          {" "}
          {model && model}
          {" "}
          Leasing ohne Anzahlung!
        </h1>
        <p
          className={`text-center headline--4 font-bold mb-2 ${
              heroTextInverted ? "xl:text-white" : ""
            }`}
        >
          Privat und gewerblich.
        </p>
        {hasOffers && (
          <>
            <div className="text-primary text-center headline--4 font-bold">
              Ab
              {" "}
              {minLeasingRate}
              {" "}
              mtl.
            </div>
            <div className="text-center">
              <FinancingDetailsOverlay
                title={cheapestProduct.title}
                buttonColor="primary"
                financingData={<FinancingData {...cheapestProduct} />}
              />
            </div>
          </>
        )}
      </div>
    </div>

    {/* hero image */}
    <div className="brand-model-hero__image-wrapper">
      <BrandModelHeroImage
        imagePath={heroImageBase}
        heroImagePosition={heroImagePosition}
        heroImageWithBackground={heroImageWithBackground}
        heroImageFullCover={heroImageFullCover}
        altText={`${brand} ${model || ""}`}
      />
    </div>

    <div
      className={`brand-model-hero__bottom-box w-full flex justify-center ${
          heroImageWithBackground
            ? "relative mt-8 xxl:mt-20 md:mb-8"
            : "xl:absolute"
        } ${heroImageFullCover ? "mt-8 xl:mt-0" : ""}`}
    >
      <div className="flex flex-col quickfinder">
        {hasOffers ? (
          <>
            <Quickfinder
              backgroundColor="transparent"
              paddingY={0}
              fixedQueryParams={{ marke: brand, modell: model }}
              headline={`Jetzt ${brand} ${model || ""} Leasing Angebote vergleichen`}
              headlineColor={`${heroTextInverted ? "xl:text-white" : null}`}
              showModelTypeSelect={showModelTypeSelect}
              modelTypes={modelTypes}
              quickfinderFilters={quickfinderFilters}
              hidePrivateBusinessOffer
            />
            <div className="mt-6 xl:mt-12 mb-15 xl:mb-0 self-center mx-5">
              <Checklist
                classNames={[`${heroTextInverted ? "xl:text-white" : ""}`]}
                list={[
                  "Nur autorisierte, seriöse Händler",
                  "Keine versteckten Kosten",
                    `Leasingvertrag bei der ${brand} Bank`,
                ]}
              />
            </div>
          </>
        ) : (
          <>
            <div className="mb-15 xl:mb-8 self-center mx-5">
              <Checklist
                classNames={[`${heroTextInverted ? "xl:text-white" : ""}`]}
                list={[
                  "Nur autorisierte, seriöse Händler",
                  "Keine versteckten Kosten",
                    `Leasingvertrag bei der ${brand} Bank`,
                ]}
              />
            </div>
            <OutOfStockNotice
              brand={brand}
              brandPageUrl={brandPageUrl}
              model={model}
            />
          </>
        )}
      </div>
    </div>
  </div>
) : (
  <div
    className="brand-model-hero relative h-full xl:mb-15"
    data-brand-name={brand}
  >
    <div>
      <Breadcrumb items={breadcrumbItems} />
      <div className="pt-6 lg:pt-15 px-5 lg:px-10">
        <h1 className="text-center mb-3 lg:mb-5">
          {brand}
          {" "}
          {model && model}
          {" "}
          Leasing ohne Anzahlung!
        </h1>
        <p className="text-center headline--4 font-bold mb-2">
          Privat und gewerblich.
        </p>
        {hasOffers && (
          <p className="text-primary text-center headline--4 font-bold">
            Ab
            {" "}
            {minLeasingRate}
            {" "}
            mtl.
          </p>
        )}
      </div>
    </div>

    {/* hero background image */}
    <div
      className="max-w-lg mx-auto bg-no-repeat bg-contain bg-center my-3"
      style={{
        height: "360px",
        backgroundImage: `url(${buildHeroImageUrls(
          heroImageBase,
          768,
          false,
        )})`,
      }}
    />

    <div className="w-full flex justify-center">
      <div className="flex flex-col quickfinder">
        {hasOffers ? (
          <>
            <Quickfinder
              backgroundColor="transparent"
              paddingY={0}
              fixedQueryParams={{ marke: brand, modell: model }}
              headline={`Jetzt ${brand} ${model || ""} Leasing Angebote vergleichen`}
              headlineColor={`${heroTextInverted ? "xl:text-white" : null}`}
              showModelTypeSelect={showModelTypeSelect}
              modelTypes={modelTypes}
            />
            <div className="mt-6 xl:mt-12 mb-15 xl:mb-0 self-center mx-5">
              <Checklist
                list={[
                  "Nur autorisierte, seriöse Händler",
                  "Keine versteckten Kosten",
                  `Leasingvertrag bei der ${brand} Bank`,
                ]}
              />
            </div>
          </>
        ) : (
          <>
            <div className="mb-15 xl:mb-8 self-center mx-5">
              <Checklist
                list={[
                  "Nur autorisierte, seriöse Händler",
                  "Keine versteckten Kosten",
                  `Leasingvertrag bei der ${brand} Bank`,
                ]}
              />
            </div>
            <OutOfStockNotice
              brand={brand}
              brandPageUrl={brandPageUrl}
              model={model}
            />
          </>
        )}
      </div>
    </div>
  </div>
));

export default withObjectFitSupportDetection(BrandModelHero);
